/**
 * Venue knowledge base.
 *
 * Two layers:
 *  - venue-facts.json — the agent-researched profile DB (700+ US/CA/EU venues:
 *    capacity, type, opened, roof, transit, knownFor, tip, aliases). Facts are
 *    best-effort: fields the researchers weren't confident in are null, and
 *    the UI renders around gaps rather than guessing.
 *  - CURATED — the hand-verified roof/transit entries below. These WIN on
 *    collision (they were written against primary sources).
 *
 * Alias keys matter: sponsor renames (Staples Center → Crypto.com Arena) are
 * the #1 reason venue lookups miss, so every profile is also indexed under
 * each alias slug.
 */
import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import { useToday } from '../lib/today';
import PROFILE_LIST from './venue-facts.json';

export interface VenueFacts {
  roof: 'outdoor' | 'indoor' | 'retractable' | 'canopy';
  transit?: string;
  /** Rebuilt under the same name: events on or after `from` are in the new building. */
  next?: { from: string; roof: VenueFacts['roof']; transit?: string };
}

export interface VenueProfile {
  slug: string;
  name: string;
  aliases?: string[];
  city: string;
  state?: string | null;
  country: string;
  type: string;
  capacity?: number | null;
  opened?: number | null;
  roof?: VenueFacts['roof'] | null;
  transit?: string | null;
  knownFor?: string | null;
  tip?: string | null;
  /** A venue rebuilt under the same name (Nissan Stadium, 2027): these fields replace the
   *  profile's from `from` (YYYY-MM-DD) on — on venue pages by today's date (profileOn),
   *  in an event's practical info by the event's date (VENUE_FACTS `next`). */
  next?: Partial<Omit<VenueProfile, 'slug' | 'name' | 'aliases' | 'next'>> & { from: string };
}

/** The profile as it stands on `day` (YYYY-MM-DD): a rebuilt venue's new facts from their date on. */
export const profileOn = (p: VenueProfile, day: string): VenueProfile => (p.next && day >= p.next.from ? { ...p, ...p.next } : p);

const venueSlug = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/** Full profiles, indexed by canonical slug AND every alias slug.
 *
 *  The prerender has the whole file (static import). The browser bundle gets
 *  an empty list (vite alias on './venue-facts.json'): each prerendered page
 *  embeds the profiles it used, and a page reached by navigation fetches
 *  /data/venue-facts.json once. */
export const VENUE_PROFILES: Record<string, VenueProfile> = {};

export const addProfiles = (list: VenueProfile[]) => {
  const valid = list.filter((p) => p?.slug && p?.name);
  // Own slugs first, so an old name of one venue ("State Farm Arena" was Payne Arena's
  // former name) can never take over another venue's real slug; an alias that two
  // profiles claim identifies neither and is skipped (owner audit, 2026-10-03).
  for (const p of valid) VENUE_PROFILES[p.slug] = VENUE_PROFILES[p.slug] ?? p;
  const claims = new Map<string, number>();
  for (const p of valid) for (const a of p.aliases ?? []) claims.set(venueSlug(a), (claims.get(venueSlug(a)) ?? 0) + 1);
  for (const p of valid) {
    for (const a of p.aliases ?? []) {
      const s = venueSlug(a);
      if (s && !VENUE_PROFILES[s] && (claims.get(s) ?? 0) === 1) VENUE_PROFILES[s] = p;
    }
    if (p.roof && !CURATED[p.slug]) {
      VENUE_FACTS[p.slug] = {
        roof: p.roof,
        transit: p.transit ?? undefined,
        ...(p.next?.roof ? { next: { from: p.next.from, roof: p.next.roof, transit: (p.next.transit ?? p.transit) || undefined } } : {}),
      };
    }
  }
  Object.assign(VENUE_FACTS, CURATED);
  venueVersion++;
  for (const fn of venueListeners) fn();
};

/** Practical-info lookup (roof + transit): researched profiles as the base,
 *  hand-verified CURATED entries winning on collision. Filled by addProfiles. */
export const VENUE_FACTS: Record<string, VenueFacts> = {};
let venueVersion = 0;
const venueListeners = new Set<() => void>();
let profilesLoaded = false;
let profilesLoading: Promise<void> | null = null;

/** Fetch the whole profile file once (browser only). Never throws. */
export const loadVenueProfiles = (): Promise<void> => {
  if (profilesLoaded || import.meta.env.SSR) return Promise.resolve();
  if (profilesLoading) return profilesLoading;
  profilesLoading = fetch('/data/venue-facts.json')
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
    .then((list: VenueProfile[]) => {
      profilesLoaded = true;
      addProfiles(list);
    })
    .catch((err) => console.warn('venue profiles failed:', err?.message ?? err))
    .finally(() => {
      profilesLoading = null;
    });
  return profilesLoading;
};

/** Prerender collector for venue profiles (see src/data/store.ts for events). */
export const VenueCollectorContext = createContext<Set<string> | null>(null);

/**
 * A venue's profile by slug. In the prerender it records the slug so the
 * page embeds the profile; in the browser it fetches the file if the profile
 * isn't there yet and re-renders when it lands.
 */
export const useVenueProfile = (slug: string | undefined): VenueProfile | undefined => {
  const collector = useContext(VenueCollectorContext);
  // A rebuilt venue (profile `next`) shows its new facts from their date on.
  const today = useToday();
  useSyncExternalStore(
    (fn) => {
      venueListeners.add(fn);
      return () => venueListeners.delete(fn);
    },
    () => venueVersion,
    () => venueVersion
  );
  const key = (slug ?? '').toLowerCase();
  const profile = key ? VENUE_PROFILES[key] : undefined;
  if (collector && profile) collector.add(profile.slug);
  useEffect(() => {
    if (key && !VENUE_PROFILES[key] && !profilesLoaded) void loadVenueProfiles();
  }, [key]);
  return profile && profileOn(profile, today);
};

const CURATED: Record<string, VenueFacts> = {
  // — New York / New Jersey
  'madison-square-garden': { roof: 'indoor', transit: 'Penn Station is directly beneath the arena (LIRR, NJ Transit, subway 1/2/3, A/C/E).' },
  'radio-city-music-hall': { roof: 'indoor', transit: '47-50th St Rockefeller Center station (B/D/F/M) is at the door.' },
  'beacon-theatre': { roof: 'indoor', transit: '72nd St station (1/2/3) is two blocks away.' },
  'yankee-stadium': { roof: 'outdoor', transit: '161 St-Yankee Stadium station (4, B/D) is across the street.' },
  'citi-field': { roof: 'outdoor', transit: 'Mets-Willets Point (7 train and LIRR) is at the gates.' },
  'barclays-center': { roof: 'indoor', transit: 'Atlantic Av-Barclays Ctr sits directly below (2/3/4/5, B/D/N/Q/R, LIRR).' },
  'metlife-stadium': { roof: 'outdoor', transit: 'NJ Transit runs Meadowlands rail service from Secaucus on event days.' },
  'forest-hills-stadium': { roof: 'outdoor', transit: 'Forest Hills-71 Av (E/F) and the LIRR Forest Hills stop are a short walk.' },
  'ubs-arena': { roof: 'indoor', transit: 'LIRR Elmont-UBS Arena station serves event days.' },
  'prudential-center': { roof: 'indoor', transit: 'Newark Penn Station (PATH, NJ Transit, Amtrak) is two blocks away.' },
  // — Chicago
  'wrigley-field': { roof: 'outdoor', transit: 'Addison station on the Red Line is one block from the park.' },
  'united-center': { roof: 'indoor', transit: 'CTA runs the #19 United Center Express on event days; Illinois Medical District (Blue Line) is a 15-minute walk.' },
  'soldier-field': { roof: 'outdoor', transit: 'Roosevelt station (Red/Orange/Green) plus the #146 bus down to the museum campus.' },
  'guaranteed-rate-field': { roof: 'outdoor', transit: 'Sox-35th on the Red Line is across the street.' },
  // — Los Angeles area
  'dodger-stadium': { roof: 'outdoor', transit: 'The free Dodger Stadium Express bus runs from Union Station on game days — parking lots exit slowly.' },
  'sofi-stadium': { roof: 'canopy', transit: 'K Line to Downtown Inglewood plus a shuttle/walk; rideshare zones fill fast after the show.' },
  'crypto-com-arena': { roof: 'indoor', transit: 'Pico station (A/E lines) is a block away.' },
  'kia-forum': { roof: 'indoor', transit: 'K Line to Downtown Inglewood, then a short shuttle or 20-minute walk.' },
  'hollywood-bowl': { roof: 'outdoor', transit: 'Park-and-ride shuttles are the standard move — on-site parking is stacked and slow.' },
  'intuit-dome': { roof: 'indoor', transit: 'Adjacent to SoFi — K Line to Downtown Inglewood plus shuttle.' },
  'greek-theatre': { roof: 'outdoor', transit: 'Stacked on-site parking; leave early or use rideshare to Los Feliz.' },
  'bmo-stadium': { roof: 'outdoor', transit: 'Expo Park/USC station (E Line) is across the street.' },
  // — Bay Area
  'chase-center': { roof: 'indoor', transit: 'UCSF/Chase Center platform on the Muni T line is at the plaza.' },
  'oracle-park': { roof: 'outdoor', transit: '2nd & King Caltrain station and Muni T are right outside.' },
  'levi-s-stadium': { roof: 'outdoor', transit: 'VTA light rail (Great America station) serves the stadium.' },
  // — Boston
  'fenway-park': { roof: 'outdoor', transit: 'Kenmore station (Green B/C/D) is a 5-minute walk.' },
  'td-garden': { roof: 'indoor', transit: 'North Station (Orange/Green, commuter rail) is directly below.' },
  // — Philadelphia
  'wells-fargo-center': { roof: 'indoor', transit: 'NRG station at the end of the Broad Street Line serves the whole sports complex.' },
  'citizens-bank-park': { roof: 'outdoor', transit: 'NRG station on the Broad Street Line, then a short walk.' },
  'lincoln-financial-field': { roof: 'outdoor', transit: 'NRG station on the Broad Street Line.' },
  // — Washington DC
  'capital-one-arena': { roof: 'indoor', transit: 'Gallery Place-Chinatown Metro is directly beneath the arena.' },
  'nationals-park': { roof: 'outdoor', transit: 'Navy Yard-Ballpark Metro (Green) is half a block away.' },
  'the-anthem': { roof: 'indoor', transit: 'Waterfront Metro (Green) is a 10-minute walk along The Wharf.' },
  'northwest-stadium': { roof: 'outdoor', transit: 'Morgan Boulevard Metro (Blue/Silver) is about a 15-minute walk.' },
  // — Texas
  'at-t-stadium': { roof: 'retractable', transit: 'No rail service — plan parking ahead or rideshare from Arlington lots.' },
  'globe-life-field': { roof: 'retractable', transit: 'No rail service — arrive early, lots share traffic with AT&T Stadium.' },
  'american-airlines-center': { roof: 'indoor', transit: 'Victory station (DART/TRE) is at the plaza.' },
  'toyota-center': { roof: 'indoor', transit: 'Bell or Central stations (METRORail Red) are a few blocks away.' },
  'daikin-park': { roof: 'retractable', transit: 'Preston station (METRORail Red) is two blocks away.' },
  'nrg-stadium': { roof: 'retractable', transit: 'Stadium Park/Astrodome station on the METRORail Red Line.' },
  'moody-center': { roof: 'indoor', transit: 'A short walk from UT campus; rideshare zones on Red River St.' },
  'frost-bank-center': { roof: 'indoor', transit: 'Limited transit — plan parking or rideshare.' },
  // — Florida
  'kaseya-center': { roof: 'indoor', transit: 'Freedom Tower Metromover station is across Biscayne Blvd.' },
  'loandepot-park': { roof: 'retractable', transit: 'Limited transit — drive or rideshare to Little Havana.' },
  'hard-rock-stadium': { roof: 'canopy', transit: 'No rail — event shuttles and rideshare; the canopy covers seats, not the field.' },
  'amalie-arena': { roof: 'indoor', transit: 'TECO streetcar connects downtown Tampa to the arena district.' },
  'kia-center': { roof: 'indoor', transit: 'SunRail Church Street station is a 10-minute walk.' },
  // — Atlanta
  'mercedes-benz-stadium': { roof: 'retractable', transit: 'GWCC/CNN Center and Vine City MARTA stations flank the building.' },
  'state-farm-arena': { roof: 'indoor', transit: 'GWCC/CNN Center MARTA station is next door.' },
  'truist-park': { roof: 'outdoor', transit: 'No rail — the Battery lots and rideshare; leave buffer time on I-285.' },
  // — Nashville
  'bridgestone-arena': { roof: 'indoor', transit: 'Downtown — most visitors walk from Broadway hotels.' },
  // The enclosed (fixed-roof) Nissan Stadium replaces the 1999 bowl under the same name:
  // substantial completion 2027-02-17, first major concert 2027-07-03 (WKRN / Titans, 2026).
  'nissan-stadium': {
    roof: 'outdoor',
    transit: 'Walk across the pedestrian bridge from downtown Broadway.',
    next: { from: '2027-02-17', roof: 'indoor', transit: 'Walk across the pedestrian bridge from downtown Broadway.' },
  },
  'ryman-auditorium': { roof: 'indoor', transit: 'Downtown — walkable from Broadway.' },
  // — Las Vegas
  'allegiant-stadium': { roof: 'indoor', transit: 'A 15-minute walk over the Hacienda bridge from the Strip.' },
  't-mobile-arena': { roof: 'indoor', transit: 'On the Strip behind Park MGM — walk or use the Park & T-Mobile rideshare decks.' },
  'sphere': { roof: 'indoor', transit: 'Connected to The Venetian by pedestrian bridge.' },
  'dolby-live': { roof: 'indoor', transit: 'Inside Park MGM on the Strip.' },
  // — Seattle / Denver / Phoenix
  'climate-pledge-arena': { roof: 'indoor', transit: 'Monorail from Westlake Center lands at Seattle Center.' },
  't-mobile-park': { roof: 'retractable', transit: "Stadium station (Link 1 Line) or a short walk from King Street." },
  'lumen-field': { roof: 'outdoor', transit: 'Stadium and Int’l District Link stations are adjacent.' },
  'ball-arena': { roof: 'indoor', transit: 'Ball Arena/Elitch Gardens station (E/W lines).' },
  'red-rocks-amphitheatre': { roof: 'outdoor', transit: 'No transit — drive early or book a shuttle from Denver; the hike up is real.' },
  'footprint-center': { roof: 'indoor', transit: 'Washington/Central Ave Valley Metro stop is a block away.' },
  'chase-field': { roof: 'retractable', transit: 'Jefferson/1st Ave Valley Metro stop is across the street.' },
  // — Midwest / other
  'little-caesars-arena': { roof: 'indoor', transit: 'QLine streetcar stops at the arena on Woodward Ave.' },
  'comerica-park': { roof: 'outdoor', transit: 'QLine on Woodward serves the ballpark district.' },
  'pnc-park': { roof: 'outdoor', transit: 'Walk the Roberto Clemente Bridge from downtown — it closes to cars on game days.' },
  'ppg-paints-arena': { roof: 'indoor', transit: 'Steel Plaza T station is a 5-minute walk.' },
  'kauffman-stadium': { roof: 'outdoor', transit: 'No rail — I-70 lots; tailgating culture is strong.' },
  'target-field': { roof: 'outdoor', transit: 'Target Field station (Blue/Green lines) is at the gate.' },
  'target-center': { roof: 'indoor', transit: 'Warehouse District/Hennepin Ave station (Blue/Green).' },
  'fiserv-forum': { roof: 'indoor', transit: 'The Hop streetcar and downtown walkability.' },
  'american-family-field': { roof: 'retractable', transit: 'No rail — legendary tailgating lots; arrive early.' },
  // — Canada (tour stops in catalog)
  'scotiabank-arena': { roof: 'indoor', transit: 'Union Station (TTC, GO) connects underground via the PATH.' },
  'rogers-centre': { roof: 'retractable', transit: 'Union Station is a covered 10-minute walk via SkyWalk.' },
  'bell-centre': { roof: 'indoor', transit: 'Bonaventure and Lucien-L’Allier Metro stations are adjacent.' },
  'rogers-arena': { roof: 'indoor', transit: 'Stadium-Chinatown SkyTrain station is a 5-minute walk.' },
  'bc-place': { roof: 'retractable', transit: 'Stadium-Chinatown SkyTrain station is adjacent.' },
};

// Seed: the static file on the server, an empty stub in the browser.
addProfiles(PROFILE_LIST as VenueProfile[]);
